// UploadProgressNotice — keeps ONE persistent notice open while any
// `document-upload` process (see @ds/actions/useUploadProcess) still has items
// in flight: "N dokumentów jest wgrywanych do systemu". Renders nothing itself;
// mounted once by BackgroundWorkMonitor, next to ActionMonitorButton (inside
// AntdApp + ProcessProvider).
// Closes automatically when the in-flight count hits 0. The keyed / non-closable
// / never-auto-dismissing contract lives in @ds/actions/usePersistentNotice.
import React, { useEffect } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useProcesses } from "@ds/actions/useProcesses";
import { countInFlightUploads } from "@ds/actions/useUploadProcess";
import { usePersistentNotice } from "@ds/actions/usePersistentNotice";

export const UPLOAD_NOTICE_KEY = "document-upload-progress";

/** `hidden`: the „Zadania w tle” drawer is open — it shows the same progress and the
 *  notice would cover its top. The notice comes back once it closes, if still needed. */
export const UploadProgressNotice: React.FC<{ hidden?: boolean }> = ({ hidden = false }) => {
  const notice = usePersistentNotice();
  const { processes } = useProcesses();
  const { t } = useTranslation();
  const count = countInFlightUploads(processes);

  useEffect(() => {
    if (count === 0 || hidden) {
      notice.close(UPLOAD_NOTICE_KEY);
      return;
    }
    // Same key ⇒ the notice updates in place (count changes, no flicker).
    notice.open({
      key: UPLOAD_NOTICE_KEY,
      testId: "upload-progress-notice",
      content: t("documents.upload.in_progress", { count }),
      icon: <LoadingOutlined spin />,
    });
  }, [count, hidden, notice, t]);

  useEffect(() => () => notice.close(UPLOAD_NOTICE_KEY), [notice]);

  return null;
};
